import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Collapse,
} from '@mui/material'
import {
  History,
  Search,
  FilterList,
  Refresh,
  Add,
  Edit,
  Delete,
  Login,
  Logout,
  Visibility,
  Download,
  ExpandMore,
  ExpandLess,
  Person,
  Computer,
  AccessTime,
  DataObject,
} from '@mui/icons-material'
import { PageHeader, Button, Card, EmptyState } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'

const ACTION_ICONS = {
  create: <Add />,
  update: <Edit />,
  delete: <Delete />,
  login: <Login />,
  logout: <Logout />,
  view: <Visibility />,
  export: <Download />,
}

const ACTION_COLORS = {
  create: '#10b981',
  update: '#3b82f6',
  delete: '#ef4444',
  login: '#8b5cf6',
  logout: '#6b7280',
  view: '#0891b2',
  export: '#f59e0b',
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

const ChangesViewer = ({ changes }) => {
  const [open, setOpen] = useState(false)

  if (!changes || Object.keys(changes).length === 0) {
    return <Typography variant="caption" color="text.secondary">No changes</Typography>
  }

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        onClick={() => setOpen(!open)}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{ textTransform: 'none', p: 0 }}
      >
        {Object.keys(changes).length} field{Object.keys(changes).length > 1 ? 's' : ''}
      </Button>
      <Collapse in={open}>
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            maxHeight: 200,
            overflow: 'auto',
          }}
        >
          {Object.entries(changes).map(([field, value]) => (
            <Box key={field} mb={0.5}>
              <Typography variant="caption" fontWeight={600} color="primary">
                {field}:
              </Typography>
              {typeof value === 'object' ? (
                <Box ml={1}>
                  {value.old !== undefined && (
                    <Typography variant="caption" display="block" sx={{ color: '#ef4444' }}>
                      - {JSON.stringify(value.old)}
                    </Typography>
                  )}
                  {value.new !== undefined && (
                    <Typography variant="caption" display="block" sx={{ color: '#10b981' }}>
                      + {JSON.stringify(value.new)}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" ml={1}>
                  {JSON.stringify(value)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

const AuditLogDetailDialog = ({ log, open, onClose }) => {
  if (!log) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar
            sx={{
              backgroundColor: `${ACTION_COLORS[log.action]}20`,
              color: ACTION_COLORS[log.action],
              width: 32,
              height: 32,
            }}
          >
            {ACTION_ICONS[log.action] || <History />}
          </Avatar>
          Audit Log Details
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Person fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">User</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {log.user_display || 'System'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <AccessTime fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDateTime(log.timestamp)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <DataObject fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Model</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {log.model_name}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip
                label={log.action}
                size="small"
                sx={{
                  backgroundColor: `${ACTION_COLORS[log.action]}20`,
                  color: ACTION_COLORS[log.action],
                  fontWeight: 600,
                  textTransform: 'capitalize',
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Object</Typography>
            <Typography variant="body2" fontWeight={500}>
              {log.object_repr || `ID: ${log.object_id}`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Computer fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">IP Address</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {log.ip_address || '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {log.user_agent && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">User Agent</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {log.user_agent}
              </Typography>
            </Grid>
          )}
          {log.changes && Object.keys(log.changes).length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Changes
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre style={{ margin: 0 }}>{JSON.stringify(log.changes, null, 2)}</pre>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

const AuditLogsList = () => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [actionFilter, setActionFilter] = useState('')
  const [modelFilter, setModelFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedLog, setSelectedLog] = useState(null)
  const [summary, setSummary] = useState(null)
  const { hasPermission } = useAuth()

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true)
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
      }
      if (actionFilter) params.action = actionFilter
      if (modelFilter) params.model_name = modelFilter
      if (searchQuery) params.search = searchQuery

      const response = await api.get('/common/audit-logs/', { params })
      setLogs(response.data.results || response.data)
      setTotalCount(response.data.count || response.data.length)
    } catch (error) {
      console.error('Error fetching audit logs:', error)
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view audit logs')
      } else {
        toast.error('Failed to load audit logs')
      }
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, actionFilter, modelFilter, searchQuery])

  const fetchSummary = async () => {
    try {
      const response = await api.get('/common/audit-logs/summary/')
      setSummary(response.data)
    } catch (error) {
      console.error('Error fetching summary:', error)
    }
  }

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  useEffect(() => {
    fetchSummary()
  }, [])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    setPage(0)
    fetchLogs()
  }

  const handleRefresh = () => {
    fetchLogs()
    fetchSummary()
    toast.success('Audit logs refreshed')
  }

  // Get unique models from summary
  const modelOptions = summary?.model_counts?.map((m) => m.model_name) || []

  return (
    <Box>
      <PageHeader
        title="Audit Logs"
        subtitle="Track all system activities and changes"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Settings', path: '/settings' },
          { label: 'Audit Logs' },
        ]}
        actions={
          <Button startIcon={<Refresh />} variant="outlined" onClick={handleRefresh}>
            Refresh
          </Button>
        }
      />

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary">
                {summary.total_logs?.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Logs
              </Typography>
            </Card>
          </Grid>
          {summary.action_counts?.slice(0, 3).map((action) => (
            <Grid item xs={6} sm={3} key={action.action}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: `${ACTION_COLORS[action.action]}20`,
                      color: ACTION_COLORS[action.action],
                    }}
                  >
                    {ACTION_ICONS[action.action]}
                  </Avatar>
                  <Typography variant="h5" fontWeight={700}>
                    {action.count.toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" textTransform="capitalize">
                  {action.action}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
        >
          <TextField
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value)
                setPage(0)
              }}
              label="Action"
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="create">Create</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
              <MenuItem value="login">Login</MenuItem>
              <MenuItem value="logout">Logout</MenuItem>
              <MenuItem value="view">View</MenuItem>
              <MenuItem value="export">Export</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Model</InputLabel>
            <Select
              value={modelFilter}
              onChange={(e) => {
                setModelFilter(e.target.value)
                setPage(0)
              }}
              label="Model"
            >
              <MenuItem value="">All Models</MenuItem>
              {modelOptions.map((model) => (
                <MenuItem key={model} value={model}>
                  {model}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button type="submit" variant="contained" startIcon={<FilterList />}>
            Filter
          </Button>
        </Box>
      </Card>

      {/* Logs Table */}
      <Card sx={{ p: 0 }}>
        {loading ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">Loading audit logs...</Typography>
          </Box>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={History}
            title="No audit logs found"
            description="No logs match your current filters"
          />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Object</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Changes</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell>
                        <Tooltip title={formatDateTime(log.timestamp)}>
                          <Typography variant="body2">
                            {formatTimeAgo(log.timestamp)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {log.user_display || 'System'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={ACTION_ICONS[log.action]}
                          label={log.action}
                          size="small"
                          sx={{
                            backgroundColor: `${ACTION_COLORS[log.action]}20`,
                            color: ACTION_COLORS[log.action],
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            '& .MuiChip-icon': {
                              color: 'inherit',
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.model_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {log.object_repr || log.object_id || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {log.ip_address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ChangesViewer changes={log.changes} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Card>

      {/* Detail Dialog */}
      <AuditLogDetailDialog
        log={selectedLog}
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </Box>
  )
}

export default AuditLogsList
